export type SocialPublishPlatform = 'instagram' | 'tiktok' | 'youtube';

const STORAGE_PREFIX = 'thspian_social_publish_lock';
/** Prevent duplicate publish attempts for the same submission + platform */
export const SOCIAL_PUBLISH_LOCK_MS = 5 * 60 * 1000;

interface PlatformLock {
    postId: string;
    expiresAt: number;
}

type SubmissionLocks = Partial<Record<SocialPublishPlatform, PlatformLock>>;

function storageKey(submissionId: string): string {
    return `${STORAGE_PREFIX}:${submissionId}`;
}

function readLocks(submissionId: string): SubmissionLocks {
    if (typeof window === 'undefined' || !submissionId) return {};
    try {
        const raw = localStorage.getItem(storageKey(submissionId));
        if (!raw) return {};
        return JSON.parse(raw) as SubmissionLocks;
    } catch {
        return {};
    }
}

function writeLocks(submissionId: string, locks: SubmissionLocks): void {
    if (typeof window === 'undefined' || !submissionId) return;
    const pruned = pruneExpired(locks);
    if (Object.keys(pruned).length === 0) {
        localStorage.removeItem(storageKey(submissionId));
        return;
    }
    localStorage.setItem(storageKey(submissionId), JSON.stringify(pruned));
}

function pruneExpired(locks: SubmissionLocks): SubmissionLocks {
    const now = Date.now();
    const next: SubmissionLocks = {};
    for (const [platform, entry] of Object.entries(locks) as [SocialPublishPlatform, PlatformLock][]) {
        if (entry && entry.expiresAt > now) {
            next[platform] = entry;
        }
    }
    return next;
}

export function setSocialPublishLock(submissionId: string, platform: SocialPublishPlatform, postId: string): void {
    const locks = readLocks(submissionId);
    locks[platform] = {
        postId,
        expiresAt: Date.now() + SOCIAL_PUBLISH_LOCK_MS,
    };
    writeLocks(submissionId, locks);
}

export function isSocialPublishLocked(submissionId: string, platform?: SocialPublishPlatform): boolean {
    const locks = pruneExpired(readLocks(submissionId));
    if (platform) {
        return Boolean(locks[platform]);
    }
    return Object.keys(locks).length > 0;
}

export function getSocialPublishLock(submissionId: string, platform: SocialPublishPlatform): PlatformLock | undefined {
    return pruneExpired(readLocks(submissionId))[platform];
}

export function clearSocialPublishLock(submissionId: string, platform: SocialPublishPlatform): void {
    const locks = readLocks(submissionId);
    delete locks[platform];
    writeLocks(submissionId, locks);
}
